import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clubsBookingApi } from '@/api/clubs-booking';
import { AdminSection } from '@/components/admin/AdminLayout';
import AdminShell from '@/components/admin/AdminShell';

const UsersCreatePage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'user' as 'admin' | 'user',
    password: '',
    confirmPassword: '',
  });

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSaving(true);

    try {
      await clubsBookingApi.createOrganizationUser(form);
      navigate('/users');
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setError('Users API is unavailable on this backend (missing POST /v1/auth/users). Restart/update the API service.');
        setSaving(false);
        return;
      }

      const rawError = err?.response?.data?.errors;
      const messageText = err?.response?.data?.message;

      if (rawError && typeof rawError === 'object' && !Array.isArray(rawError)) {
        const firstValidationError = Object.values(rawError)[0];
        setError(String(firstValidationError || 'Could not add user'));
      } else {
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not add user');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title="Users" subtitle="Add a team member and choose what access level they should have">
      <AdminSection>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Add user</h2>
          <Link to="/users" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Back to list</Link>
        </div>
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <label className="text-sm text-slate-600">Full name<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.name} onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))} required /></label>
          <label className="text-sm text-slate-600">Email<input type="email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.email} onChange={(e) => setForm((v) => ({ ...v, email: e.target.value }))} required /></label>
          <label className="text-sm text-slate-600">
            Role
            <select className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.role} onChange={(e) => setForm((v) => ({ ...v, role: e.target.value as 'admin' | 'user' }))}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <label className="text-sm text-slate-600">Password<input type="password" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.password} onChange={(e) => setForm((v) => ({ ...v, password: e.target.value }))} minLength={6} required /></label>
          <label className="text-sm text-slate-600">Confirm password<input type="password" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.confirmPassword} onChange={(e) => setForm((v) => ({ ...v, confirmPassword: e.target.value }))} minLength={6} required /></label>
          <p className="text-sm text-slate-500 md:col-span-2">
            Admin users can work in the admin area. User users have a more limited access level.
          </p>
          <div className="md:col-span-2"><button className="rounded-lg bg-[#1F8F5F] px-5 py-2.5 font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60" disabled={saving}>{saving ? 'Adding...' : 'Add user'}</button></div>
        </form>
      </AdminSection>
    </AdminShell>
  );
};

export default UsersCreatePage;


